import * as React from 'react';
import { twMerge } from 'tailwind-merge';
import { buttonVariants, fieldControl, type OppButtonVariantProps } from './controlStyles';

interface OppButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    OppButtonVariantProps {
  /** Renders an inline spinner and disables interaction until it clears. */
  loading?: boolean;
}

export const OppButton = React.forwardRef<HTMLButtonElement, OppButtonProps>(
  ({ className, variant, size, loading, disabled, children, ...props }, ref) => (
    <button
      ref={ref}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      className={twMerge(buttonVariants({ variant, size }), className)}
      {...props}
    >
      {loading ? (
        <span
          aria-hidden="true"
          className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
        />
      ) : (
        children
      )}
    </button>
  ),
);
OppButton.displayName = 'OppButton';

interface OppInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  /** Marks the field as invalid and applies the danger ring. */
  invalid?: boolean;
}

export const OppInput = React.forwardRef<HTMLInputElement, OppInputProps>(
  ({ className, invalid, ...props }, ref) => (
    <input
      ref={ref}
      aria-invalid={invalid || undefined}
      className={twMerge(
        fieldControl,
        invalid && 'border-[hsl(var(--opp-danger-fg))] ring-1 ring-[hsl(var(--opp-danger-fg))]',
        className,
      )}
      {...props}
    />
  ),
);
OppInput.displayName = 'OppInput';

interface OppTextareaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement> {
  invalid?: boolean;
}

export const OppTextarea = React.forwardRef<HTMLTextAreaElement, OppTextareaProps>(
  ({ className, invalid, ...props }, ref) => (
    <textarea
      ref={ref}
      aria-invalid={invalid || undefined}
      className={twMerge(
        fieldControl,
        'min-h-20',
        invalid && 'border-[hsl(var(--opp-danger-fg))] ring-1 ring-[hsl(var(--opp-danger-fg))]',
        className,
      )}
      {...props}
    />
  ),
);
OppTextarea.displayName = 'OppTextarea';

interface OppSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  invalid?: boolean;
}

export const OppSelect = React.forwardRef<HTMLSelectElement, OppSelectProps>(
  ({ className, invalid, ...props }, ref) => (
    <select
      ref={ref}
      aria-invalid={invalid || undefined}
      className={twMerge(
        fieldControl,
        invalid && 'border-[hsl(var(--opp-danger-fg))] ring-1 ring-[hsl(var(--opp-danger-fg))]',
        className,
      )}
      {...props}
    />
  ),
);
OppSelect.displayName = 'OppSelect';
